import { useState, useEffect, useCallback, useRef } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Users, Plus, Bell, CheckSquare, Trash2, Clock, RefreshCw, QrCode, MessageCircle, Settings, Smartphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });
const STATUS_COLORS = { waiting: 'bg-amber-100 text-amber-700', notified: 'bg-blue-100 text-blue-700', seated: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-gray-100 text-gray-500' };
const STATUS_LABEL = { waiting: 'Esperando', notified: 'Notificado', seated: 'Sentado', cancelled: 'Cancelado' };
const EMPTY_ENTRY = { name: '', party_size: 2, phone: '', notes: '', priority: 'normal' };

function WaitTime({ minutes }) {
  const color = minutes < 15 ? 'text-emerald-600' : minutes < 30 ? 'text-amber-600' : 'text-red-600';
  return <span className={`font-bold ${color}`}>{minutes} min</span>;
}

export default function DigitalWalkIn() {
  const [queue, setQueue] = useState([]);
  const [config, setConfig] = useState({ avg_wait_minutes: 20, auto_notify: true, whatsapp_enabled: true });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [elapsed, setElapsed] = useState({});
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [qRes, cfgRes] = await Promise.all([
        fetch(`${API_URL}/api/walkin/queue`, { headers: authHeader() }),
        fetch(`${API_URL}/api/walkin/config`, { headers: authHeader() }),
      ]);
      if (qRes.ok) setQueue(await qRes.json());
      if (cfgRes.ok) setConfig(await cfgRes.json());
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = { ...prev };
        queue.forEach(e => {
          if (e.status === 'waiting' && e.created_at) {
            const mins = Math.floor((Date.now() - new Date(e.created_at).getTime()) / 60000);
            next[e.id] = mins;
          }
        });
        return next;
      });
    }, 30000);
    return () => clearInterval(timerRef.current);
  }, [load, queue]);

  // Compute elapsed on load
  useEffect(() => {
    const initial = {};
    queue.forEach(e => {
      if (e.created_at) initial[e.id] = Math.floor((Date.now() - new Date(e.created_at).getTime()) / 60000);
    });
    setElapsed(initial);
  }, [queue]);

  const addToQueue = async () => {
    if (!form.name) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/walkin/queue`, { method: 'POST', headers: authHeader(), body: JSON.stringify(form) });
      if (res.ok) {
        const entry = await res.json();
        toast.success(`${form.name} agregado — posición #${entry.position}`);
        setShowModal(false);
        setForm(EMPTY_ENTRY);
        load();
      }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/walkin/queue/${id}/status`, { method: 'PUT', headers: authHeader(), body: JSON.stringify({ status }) });
    const labels = { notified: 'Comensal notificado', seated: 'Comensal sentado ✓', cancelled: 'Entrada cancelada' };
    toast.success(labels[status] || 'Actualizado');
    load();
  };

  const removeEntry = async (id) => {
    await fetch(`${API_URL}/api/walkin/queue/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Entrada eliminada');
    load();
  };

  const saveConfig = async () => {
    await fetch(`${API_URL}/api/walkin/config`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(config) });
    toast.success('Configuración guardada');
    setShowConfig(false);
  };

  const waiting = queue.filter(e => e.status === 'waiting');
  const notified = queue.filter(e => e.status === 'notified');
  const done = queue.filter(e => ['seated', 'cancelled'].includes(e.status));
  const totalGuests = waiting.reduce((s, e) => s + (Number(e.party_size) || 0), 0);
  const avgWait = waiting.length > 0 ? Math.round(waiting.length * config.avg_wait_minutes / 2) : 0;

  const stats = [
    { label: 'En espera', value: waiting.length, icon: Clock, color: 'text-amber-500' },
    { label: 'Notificados', value: notified.length, icon: Bell, color: 'text-blue-500' },
    { label: 'Total personas', value: totalGuests, icon: Users, color: 'text-purple-500' },
    { label: 'Espera promedio', value: `${config.avg_wait_minutes} min`, icon: RefreshCw, color: 'text-gray-500' },
  ];

  const EntryCard = ({ entry }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm ${entry.priority === 'vip' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              #{entry.position}
            </div>
            <div>
              <p className="font-bold text-gray-900">{entry.name}</p>
              <p className="text-xs text-gray-500">{entry.party_size} personas{entry.phone ? ` · ${entry.phone}` : ''}</p>
            </div>
          </div>
          <Badge className={STATUS_COLORS[entry.status]}>{STATUS_LABEL[entry.status]}</Badge>
        </div>
        {elapsed[entry.id] !== undefined && entry.status === 'waiting' && (
          <p className="text-xs text-gray-400 mb-2">⏱ Esperando <WaitTime minutes={elapsed[entry.id]} /></p>
        )}
        {entry.notes && <p className="text-xs text-gray-400 mb-2 bg-gray-50 rounded px-2 py-1">📝 {entry.notes}</p>}
        <div className="flex gap-2 flex-wrap">
          {entry.status === 'waiting' && (
            <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white gap-1 flex-1" onClick={() => updateStatus(entry.id, 'notified')}>
              <Bell className="w-3.5 h-3.5" /> Notificar
            </Button>
          )}
          {['waiting', 'notified'].includes(entry.status) && (
            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 flex-1" onClick={() => updateStatus(entry.id, 'seated')}>
              <CheckSquare className="w-3.5 h-3.5" /> Sentar
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => removeEntry(entry.id)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Fila Digital Walk-In" subtitle="Gestiona la cola de clientes sin reservación en tiempo real" helpSection="walk-in" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={i}><CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-7 h-7 ${s.color}`} />
              <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${waiting.length > 0 ? 'bg-amber-500 animate-pulse' : 'bg-gray-300'}`} />
            <span className="text-sm font-medium text-gray-600">{waiting.length > 0 ? `${waiting.length} grupos esperando` : 'Cola vacía'}</span>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowConfig(true)} variant="outline" className="gap-1"><Settings className="w-4 h-4" /> Config.</Button>
            <Button onClick={load} variant="outline" className="gap-1"><RefreshCw className="w-4 h-4" /> Actualizar</Button>
            <Button onClick={() => { setForm(EMPTY_ENTRY); setShowModal(true); }} className="bg-orange-500 hover:bg-orange-600 text-white gap-1"><Plus className="w-4 h-4" /> Agregar</Button>
          </div>
        </div>

        <Tabs defaultValue="waiting">
          <TabsList>
            <TabsTrigger value="waiting">Esperando ({waiting.length})</TabsTrigger>
            <TabsTrigger value="notified">Notificados ({notified.length})</TabsTrigger>
            <TabsTrigger value="done">Completados</TabsTrigger>
          </TabsList>

          <TabsContent value="waiting" className="mt-4">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div>
            : waiting.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Cola vacía</p>
                <p className="text-sm">No hay walk-ins esperando</p>
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setShowModal(true)}><Plus className="w-4 h-4 mr-1" /> Agregar walk-in</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                {waiting.map(e => <EntryCard key={e.id} entry={e} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="notified" className="mt-4">
            {notified.length === 0 ? <p className="text-center text-gray-400 py-8">No hay grupos notificados</p>
            : <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{notified.map(e => <EntryCard key={e.id} entry={e} />)}</div>}
          </TabsContent>

          <TabsContent value="done" className="mt-4">
            {done.length === 0 ? <p className="text-center text-gray-400 py-8">No hay registros completados hoy</p>
            : <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{done.map(e => <EntryCard key={e.id} entry={e} />)}</div>}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Agregar Walk-In</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nombre del cliente" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Personas</Label><Input type="number" min="1" max="20" value={form.party_size} onChange={e => setForm(p => ({ ...p, party_size: Number(e.target.value) }))} /></div>
              <div><Label>WhatsApp / Teléfono</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+52 55..." /></div>
            </div>
            <div><Label>Prioridad</Label>
              <div className="flex gap-2 mt-1">
                {['normal', 'vip'].map(p => (
                  <button key={p} type="button" onClick={() => setForm(prev => ({ ...prev, priority: p }))}
                    className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium capitalize transition-all ${form.priority === p ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>{p === 'vip' ? '⭐ VIP' : 'Normal'}</button>
                ))}
              </div>
            </div>
            <div><Label>Notas</Label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Alérgenos, ocasión especial..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={addToQueue} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Agregar a la fila'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Modal */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Configuración de la Fila</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Tiempo de espera promedio (minutos)</Label><Input type="number" min="5" max="120" value={config.avg_wait_minutes} onChange={e => setConfig(p => ({ ...p, avg_wait_minutes: Number(e.target.value) }))} /></div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <MessageCircle className="w-5 h-5 text-green-500" />
              <div className="flex-1">
                <p className="font-medium text-sm">Notificación por WhatsApp</p>
                <p className="text-xs text-gray-500">Requiere integración Kapso activa</p>
              </div>
              <input type="checkbox" checked={config.whatsapp_enabled} onChange={e => setConfig(p => ({ ...p, whatsapp_enabled: e.target.checked }))} className="rounded" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)}>Cancelar</Button>
            <Button onClick={saveConfig} className="bg-orange-500 hover:bg-orange-600 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
